import { TZDate } from '@date-fns/tz'
import type { V2EntityVersionSummary } from '@idc-forms/schemas/api-v2/admin-entity-versions'
import { format, formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { ChevronDown, Flag, History, Pencil, Plus, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  useCanRestoreEntityVersion,
  useEntityVersionDetailQuery,
  useEntityVersionsQuery,
  useRestoreEntityVersion
} from '../hooks/use-entity-versions'
import type { VersionedEntityKind } from '../services/entity-versions.service'
import { VersionChanges } from './version-changes'
import { VersionRestoreButton } from './version-restore-button'

const TIME_ZONE = 'America/Guatemala'

type VersionTimelineProps = {
  kind: VersionedEntityKind
  /** Id público del expediente (`cli_`, `neg_`). */
  entityId: string
  className?: string
}

/**
 * Historial de versiones de un expediente (ADR 0029, Fase 2).
 *
 * Cada fila es una escritura. Al abrirla se pide esa versión y se muestra QUÉ cambió respecto
 * de la anterior — el snapshot no viaja en la lista porque pesa, y traerlo para todas sería un
 * megabyte para pintar fechas.
 *
 * La fila más vieja es siempre la SEMILLA: el estado con el que el expediente entró al
 * historial. No tiene cambios que mostrar porque no hay nada anterior contra qué compararla, y
 * decirlo explícitamente evita que se lea como «esta edición no tocó nada».
 */
export function VersionTimeline({ kind, entityId, className }: VersionTimelineProps) {
  const { data: versions, isLoading, isError } = useEntityVersionsQuery(kind, entityId)
  const [openVersion, setOpenVersion] = useState<number | null>(null)
  const canRestore = useCanRestoreEntityVersion(kind)
  const restore = useRestoreEntityVersion(kind, entityId)
  // La primera fila es la más nueva: el orden viene del servidor, del más reciente al más
  // viejo. Es la versión en la que el expediente está AHORA.
  const currentVersion = versions?.[0]?.version ?? null

  if (isLoading) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    )
  }

  if (isError) {
    return <p className="text-sm text-destructive">No se pudo cargar el historial.</p>
  }

  if (!versions || versions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Este expediente todavía no tiene versiones registradas.
      </p>
    )
  }

  return (
    <ol className={cn('flex flex-col', className)}>
      {versions.map((entry, index) => (
        <VersionRow
          key={entry.version}
          entry={entry}
          kind={kind}
          entityId={entityId}
          isLast={index === versions.length - 1}
          isOpen={openVersion === entry.version}
          isCurrent={entry.version === currentVersion}
          canRestore={canRestore}
          onRestore={(version) => restore.mutate(version)}
          onToggle={() =>
            setOpenVersion((current) => (current === entry.version ? null : entry.version))
          }
        />
      ))}
    </ol>
  )
}

type VersionRowProps = {
  entry: V2EntityVersionSummary
  kind: VersionedEntityKind
  entityId: string
  isLast: boolean
  isOpen: boolean
  /** La versión en la que el expediente está ahora: no se ofrece restaurarla. */
  isCurrent: boolean
  canRestore: boolean
  onRestore: (version: number) => void
  onToggle: () => void
}

function VersionRow({
  entry,
  kind,
  entityId,
  isLast,
  isOpen,
  isCurrent,
  canRestore,
  onRestore,
  onToggle
}: VersionRowProps) {
  // El detalle se pide SÓLO cuando la fila está abierta: es donde vive el snapshot.
  const { data: detail, isLoading } = useEntityVersionDetailQuery(
    kind,
    isOpen ? entityId : null,
    isOpen ? entry.version : null
  )

  const occurred = new TZDate(entry.occurredAt, TIME_ZONE)
  const Icon = iconFor(entry)

  return (
    <li className="relative flex gap-3 pb-1">
      {/* La línea del timeline no llega a la última fila: ahí el historial termina. */}
      {!isLast && <span className="absolute left-[15px] top-9 h-full w-px bg-border" aria-hidden />}

      <span className="mt-1.5 flex size-8 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground">
        <Icon className="size-4" />
      </span>

      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-medium">{titleFor(entry)}</span>
              <span className="text-xs text-muted-foreground">v{entry.version}</span>
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {format(occurred, "d 'de' MMMM yyyy, HH:mm", { locale: es })} ·{' '}
              {formatDistanceToNow(occurred, { locale: es, addSuffix: true })}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'mt-1 size-4 shrink-0 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {isOpen && (
          <div className="flex flex-col gap-3 px-2 pb-3">
            {isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : entry.seed ? (
              <p className="text-sm text-muted-foreground">
                Estado con el que el expediente entró al historial. No hay una versión anterior con
                la cual compararlo.
              </p>
            ) : (
              <VersionChanges changes={detail?.changes ?? []} />
            )}

            {/*
              La versión MÁS NUEVA no se ofrece para restaurar: el expediente ya está así, y el
              botón sólo invitaría a una escritura que no cambia nada pero suma una versión.
            */}
            {canRestore && !isCurrent ? (
              <VersionRestoreButton version={entry.version} onRestore={onRestore} />
            ) : null}
          </div>
        )}
      </div>
    </li>
  )
}

/** Quién hizo la escritura, en la voz del producto. */
function titleFor(entry: V2EntityVersionSummary): string {
  if (entry.seed) return 'Estado inicial registrado'
  if (entry.actor.type === 'system') return 'Cambio automático del sistema'
  if (entry.actor.type === 'client') return 'Cambio hecho por el cliente'
  return entry.actor.name ?? 'Cambio hecho por el equipo'
}

function iconFor(entry: V2EntityVersionSummary) {
  if (entry.seed) return Flag
  if (entry.actor.type === 'system') return Sparkles
  if (entry.version === 1) return Plus
  return entry.actor.type === 'internal' ? Pencil : History
}
